import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  Chip,
  IconButton,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  School as SchoolIcon,
  Timeline as TimelineIcon,
  PushPin as PushPinIcon,
  BarChart as BarChartIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { getSummaryCoverage } from '../../../services/aiService';

/**
 * Summary Overview Component
 * Displays metrics and table of all schools with AI summary coverage
 */
const SummaryOverview = ({ onViewSchool }) => {
  const { enqueueSnackbar } = useSnackbar();
  
  // Data state
  const [loading, setLoading] = useState(true);
  const [statistics, setStatistics] = useState(null);
  const [schools, setSchools] = useState([]);
  const [filteredSchools, setFilteredSchools] = useState([]);
  
  // Table state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [searchQuery, setSearchQuery] = useState('');
  const [coverageFilter, setCoverageFilter] = useState('all'); // all, full, partial, drop_a_pin, none

  const loadCoverageData = async () => {
    setLoading(true);
    try {
      const data = await getSummaryCoverage();
      
      if (data.success) {
        setStatistics(data.statistics);
        
        // Combine schools with and without summaries
        const allSchools = [
          ...data.schools_with_summaries,
          ...data.schools_without_summaries
        ];
        setSchools(allSchools);
        setFilteredSchools(allSchools);
      } else {
        enqueueSnackbar('Failed to load summary coverage', { variant: 'error' });
      }
    } catch (error) {
      console.error('Error loading coverage data:', error);
      enqueueSnackbar(error.message || 'Failed to load summary coverage', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const filterSchools = () => {
    let filtered = [...schools];

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(school =>
        school.name?.toLowerCase().includes(query) ||
        school.uuid?.toLowerCase().includes(query) ||
        school.teams?.some(team => team.toLowerCase().includes(query))
      );
    }

    // Apply coverage filter
    if (coverageFilter !== 'all') {
      filtered = filtered.filter(school => school.coverage_type === coverageFilter);
    }

    setFilteredSchools(filtered);
    setPage(0); // Reset to first page when filters change
  };
  
  // Load data on mount
  useEffect(() => {
    loadCoverageData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Filter schools when search or filter changes
  useEffect(() => {
    filterSchools();
  }, [searchQuery, coverageFilter, schools]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getCoverageChip = (school) => {
    if (school.coverage_type === 'none') {
      return <Chip label="No Summaries" size="small" color="error" icon={<CancelIcon />} />;
    } else if (school.coverage_type === 'full') {
      return <Chip label="Full (4 types)" size="small" color="success" icon={<CheckCircleIcon />} />;
    } else if (school.coverage_type === 'drop_a_pin') {
      return <Chip label="Drop-a-Pin" size="small" color="info" icon={<PushPinIcon />} />;
    } else if (school.coverage_type === 'partial') {
      return <Chip label="Partial" size="small" color="warning" icon={<TimelineIcon />} />;
    }
    return null;
  };

  const getSummaryTypeIcons = (school) => {
    return (
      <Box sx={{ display: 'flex', gap: 0.5 }}>
        {school.has_geographic && (
          <Chip label="Geo" size="small" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
        )}
        {school.has_competitive && (
          <Chip label="Comp" size="small" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
        )}
        {school.has_projections && (
          <Chip label="Proj" size="small" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
        )}
        {school.has_combined && (
          <Chip label="Comb" size="small" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
        )}
      </Box>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Metrics Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" color="primary">
                    {statistics?.total_schools || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Schools
                  </Typography>
                </Box>
                <SchoolIcon sx={{ fontSize: 40, color: 'primary.light', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" color="success.main">
                    {statistics?.coverage_percentage || 0}%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Coverage Rate
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {statistics?.schools_with_summaries || 0} schools
                  </Typography>
                </Box>
                <BarChartIcon sx={{ fontSize: 40, color: 'success.light', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" color="success.main">
                    {statistics?.full_summaries || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Full Summaries
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    All 4 types
                  </Typography>
                </Box>
                <CheckCircleIcon sx={{ fontSize: 40, color: 'success.light', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" color="info.main">
                    {statistics?.drop_a_pin_summaries || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Drop-a-Pin
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Geographic only
                  </Typography>
                </Box>
                <PushPinIcon sx={{ fontSize: 40, color: 'info.light', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by school name, UUID, or team..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Coverage Filter</InputLabel>
              <Select
                value={coverageFilter}
                label="Coverage Filter"
                onChange={(e) => setCoverageFilter(e.target.value)}
              >
                <MenuItem value="all">All Schools</MenuItem>
                <MenuItem value="full">Full Summaries</MenuItem>
                <MenuItem value="partial">Partial Summaries</MenuItem>
                <MenuItem value="drop_a_pin">Drop-a-Pin</MenuItem>
                <MenuItem value="none">No Summaries</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <Typography variant="body2" color="text.secondary">
              Showing {filteredSchools.length} of {schools.length} schools
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Data Table */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell><strong>School Name</strong></TableCell>
              <TableCell><strong>Team(s)</strong></TableCell>
              <TableCell><strong>Coverage</strong></TableCell>
              <TableCell><strong>Summary Types</strong></TableCell>
              <TableCell><strong>Last Generated</strong></TableCell>
              <TableCell align="center"><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredSchools
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((school) => (
                <TableRow key={school.uuid} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {school.name}
                    </Typography>
                    {school.is_drop_a_pin && (
                      <Typography variant="caption" color="text.secondary">
                        <PushPinIcon sx={{ fontSize: 12, verticalAlign: 'middle', mr: 0.5 }} />
                        Drop-a-Pin Location
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {school.teams && school.teams.length > 0 ? (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {school.teams.map((team, idx) => (
                          <Chip key={idx} label={team} size="small" variant="outlined" />
                        ))}
                      </Box>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        No team assigned
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{getCoverageChip(school)}</TableCell>
                  <TableCell>{getSummaryTypeIcons(school)}</TableCell>
                  <TableCell>
                    <Typography variant="caption">
                      {formatDate(school.last_generated)}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={() => onViewSchool(school)}
                      disabled={!onViewSchool}
                      title="View school"
                    >
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            {filteredSchools.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Box sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      No schools found matching your filters
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={filteredSchools.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>
    </Box>
  );
};

export default SummaryOverview;
